import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@core-ui/Components/ui/dialog';
import { Button } from '@core-ui/Components/ui/button';
import { Input } from '@core-ui/Components/ui/input';
import { cn } from '@core-ui-lib/utils';
import { Mic, Square, Waves } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { TwilioStatus } from '@types';

interface Props {
  open: boolean;
  loading?: boolean;

  title: string;
  onTitleChange: (v: string) => void;

  recording: boolean;
  twilioStatus: TwilioStatus;

  onCancel: () => void;
  onStartRecording: () => void | Promise<void>;
  onStopRecording: () => void | Promise<void>;

  disableCloseOnLoading?: boolean;
}

function FakeWave ({
  progress = 0,
  animated = false,
}: {
  progress?: number;
  animated?: boolean;
}): React.JSX.Element {
  const bars = 42;

  const heights = useMemo(
    () => Array.from({ length: bars }).map((_, i) => 8 + ((i * 17) % 24)),
    [],
  );

  return (
    <div className="flex items-center justify-center gap-[3px] h-16">
      {heights.map((h, i) => {
        const active = i / bars <= progress;
        return (
          <span
            key={i}
            className={cn(
              'w-[3px] rounded-sm transition-all',
              active ? 'bg-primary' : 'bg-primary/25',
              animated ? 'opacity-100' : 'opacity-90',
            )}
            style={{ height: h }}
          />
        );
      })}
    </div>
  );
}

export default function CreateVoicemailDialog ({
  open,
  loading = false,
  title,
  onTitleChange,
  recording,
  twilioStatus,
  onCancel,
  onStartRecording,
  onStopRecording,
  disableCloseOnLoading = true,
}: Props): React.JSX.Element {
  const { t } = useTranslation();

  const [fakeProgress, setFakeProgress] = useState(0);

  useEffect(() => {
    if (!open || !recording) {
      setFakeProgress(0);
      return;
    }

    const id = window.setInterval(() => {
      setFakeProgress((p) => (p >= 1 ? 0.05 : p + 0.04));
    }, 120);

    return () => {
      window.clearInterval(id);
    };
  }, [open, recording]);

  const canUseTwilio = twilioStatus === 'ready' || twilioStatus === 'busy';

  const handleOpenChange = (isOpen: boolean): void => {
    if (disableCloseOnLoading && loading) {
      return;
    }
    if (!isOpen) {
      onCancel();
    }
  };

  const handlePrimaryAction = (): void => {
    if (loading) {
      return;
    }

    const clean = title.trim();
    if (!recording) {
      if (clean === '' || !canUseTwilio) {
        return;
      }
      void onStartRecording();
      return;
    }

    void onStopRecording();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg rounded-2xl shadow-lg border border-border bg-card text-foreground p-6"
        onEscapeKeyDown={(e) => {
          if (disableCloseOnLoading && loading) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (disableCloseOnLoading && loading) {
            e.preventDefault();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !loading) {
            e.preventDefault();
            if (!recording) {
              handlePrimaryAction();
            }
          }
        }}
      >
        <DialogHeader className="mb-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-primary/20 bg-primary/10 text-primary">
              <Waves className="h-5 w-5" />
            </span>
            <div className="space-y-0.5">
              <DialogTitle className="text-lg font-semibold">
                {t('voice.voicemails.create.title', 'Create voicemail')}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                {t('voice.voicemails.create.subtitle', 'Name it and record a short message.')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-2 mt-3">
          <label className="text-sm text-muted-foreground">
            {t('voice.voicemails.create.nameLabel', 'Voicemail name')}
          </label>
          <Input
            data-qa-id="vm-name"
            placeholder={t('voice.voicemails.create.namePh', 'Voicemail name')}
            value={title}
            onChange={(e) => {
              onTitleChange(e.target.value);
            }}
            disabled={loading || recording}
          />
        </div>

        <div className="mt-6 rounded-xl border border-border bg-muted/30 px-4 py-6">
          <FakeWave progress={fakeProgress} animated={recording} />

          <div className="mt-4 flex justify-center">
            <button
              data-qa-id="start-recording"
              type="button"
              onClick={handlePrimaryAction}
              disabled={loading || (!recording && (!canUseTwilio || title.trim() === ''))}
              className={cn(
                'flex h-14 w-14 items-center justify-center rounded-full border transition',
                recording
                  ? 'bg-red-500 text-white border-red-500'
                  : 'bg-background text-primary hover:bg-muted',
                (loading || (!recording && (!canUseTwilio || title.trim() === '')))
                  ? 'opacity-60 cursor-not-allowed'
                  : 'cursor-pointer',
              )}
              aria-label={recording ? 'Stop recording' : 'Start recording'}
              title={recording ? 'Stop recording' : 'Start recording'}
            >
              {recording ? <Square className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>
          </div>

          <p className="mt-3 text-center text-xs text-muted-foreground">
            {recording
              ? t('voice.voicemails.create.recording', 'Recording… click to stop')
              : t('voice.voicemails.create.tapToRecord', 'Click to start recording')}
          </p>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button
            data-qa-id="cancel-create-vm-form-button"
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            className="border border-border text-foreground hover:bg-muted"
          >
            {t('adminConsole.common.cancel', 'Cancel')}
          </Button>

          <Button
            data-qa-id="create-vm-form-button"
            type="button"
            onClick={handlePrimaryAction}
            disabled={loading || (!recording && (title.trim() === '' || !canUseTwilio))}
            className={cn(
              'bg-primary text-primary-foreground hover:bg-primary/90',
              recording && 'bg-red-500 hover:bg-red-600',
            )}
          >
            {recording
              ? t('voice.voicemails.create.stop', 'Stop')
              : (loading ? t('settings.settingsTab.saving', 'Saving…') : t('voice.voicemails.create.start', 'Start recording'))}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
